// The Excel-style selection of a table's cells, shared by the grids that edit in place:
// grid-cells (the list tables, where an editor is fetched per cell and saved on its own) and
// multi-entry-grid (the Multibericht, whose inputs sit in the cells and post as one form).
//
// A cell is selected, not hovered: arrows, Home/End and Tab move the selection, Enter after a
// run of Tabs returns to the column the run started in, one row down. What a key does to the
// selected cell itself — open an editor, type over, clear — is the host controller's.
//
// A mixin, so the host keeps `this.selected` ({ rowId, column }) and `this.tabOrigin` as its own
// state, which its editing code reads throughout:
//
//   export default class extends GridSelection(Controller) { … }
//
// The host's table marks each row `tr[data-grid-row][data-row-id]` and each selectable cell
// `td[data-column]`. A `tabSkip` value of "editable" makes Tab skip cells without
// `data-editable`. A table nested in a row of another with the same controller is left to its
// own instance.

const MOVES = {
  ArrowDown: [1, 0],
  ArrowUp: [-1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
}

// Option+key types a character on a Mac (Option+L is "@" on a German layout). On Windows the same
// characters come from AltGr, which arrives as Ctrl+Alt and is caught by getModifierState below.
const MAC = /Mac|iPhone|iPad/.test(globalThis.navigator?.platform ?? "")
// What Alt/AltGr/Option plus a key produces when it is a shortcut rather than a character.
const MODIFIED_NON_CHARACTER = /^[a-z0-9\s]$/i

export const GridSelection = (Base) =>
  class extends Base {
    // A `hidden` row (inside a collapsed group) is not on screen, so the selection must not walk
    // into it.
    get rows() {
      return Array.from(this.element.querySelectorAll("tr[data-grid-row]:not([hidden])")).filter(
        (row) => row.closest(`[data-controller~="${this.identifier}"]`) === this.element,
      )
    }

    // The element the table scrolls in. The controller's own element, unless the host sits on a
    // form around it (multi-entry-grid).
    get gridScroller() {
      return this.element
    }

    cellsOf(row) {
      return Array.from(row.querySelectorAll("td[data-column]"))
    }

    // Re-finds a cell from the ids rather than a node reference: after a save the whole <tr> is a
    // new element, so anything held across the round trip is stale.
    findCell({ rowId, column }) {
      const row = this.element.querySelector(`tr[data-grid-row][data-row-id="${rowId}"]`)
      return row?.querySelector(`td[data-column="${CSS.escape(column)}"]`) ?? null
    }

    select(cell, { focus = true } = {}) {
      if (!cell) return

      const previous = this.selected && this.findCell(this.selected)
      if (previous && previous !== cell) previous.removeAttribute("data-selected")
      // The selected cell is the table's one tab stop. Cleared across the whole table, not just on
      // the previous selection: a host controller can hand out the stop before anything is selected
      // (position-tree does), and a second stop makes Tab land on a stale cell.
      this.element.querySelectorAll('td[data-column][tabindex="0"]').forEach((other) => {
        if (other !== cell) other.tabIndex = -1
      })

      cell.setAttribute("data-selected", "")
      cell.tabIndex = 0
      this.selected = { rowId: cell.closest("tr").dataset.rowId, column: cell.dataset.column }
      if (focus) {
        cell.focus({ preventScroll: true })
        this.scrollCellIntoView(cell)
      }
    }

    // `scrollIntoView({ inline: "nearest" })` is wrong for this table. The pinned first column and
    // the sticky header are painted OVER the scrollport, and the browser counts a cell that sits
    // underneath them as already visible — so walking back left from the right edge stops scrolling
    // as soon as the target slides behind the pinned column, and the selection keeps moving
    // invisibly. Scroll by hand instead, against a viewport inset by whatever is sticky.
    //
    // What is sticky on the left: the pinned column, and a frozen one beside it (a position grid's
    // Kurztext, from sm up — below that it scrolls, so it only counts while computed sticky).
    scrollCellIntoView(cell) {
      const scroller = this.gridScroller
      const view = scroller.getBoundingClientRect()
      const box = cell.getBoundingClientRect()
      const sticky = [
        ...scroller.querySelectorAll("thead th[data-pinned], thead th[data-frozen]"),
      ].filter((th) => getComputedStyle(th).position === "sticky")
      const header = scroller.querySelector("thead")
      // A sticky column's own cells are never behind the inset. The right edge of the last one also
      // covers a sticky leading (checkbox) column in front of them.
      const onSticky = sticky.some((th) => th.dataset.column === cell.dataset.column)
      const left =
        onSticky || sticky.length === 0
          ? view.left
          : Math.max(...sticky.map((th) => th.getBoundingClientRect().right))
      const top = view.top + (header?.getBoundingClientRect().height ?? 0)

      // `Math.min` keeps "nearest" semantics for a cell larger than the visible area: align its
      // leading edge rather than scrolling past it to bring the trailing edge in.
      if (box.left < left) scroller.scrollLeft -= left - box.left
      else if (box.right > view.right)
        scroller.scrollLeft += Math.min(box.right - view.right, box.left - left)

      if (box.top < top) scroller.scrollTop -= top - box.top
      else if (box.bottom > view.bottom)
        scroller.scrollTop += Math.min(box.bottom - view.bottom, box.top - top)
    }

    clearSelection() {
      const cell = this.selected && this.findCell(this.selected)
      if (cell) {
        cell.removeAttribute("data-selected")
        cell.tabIndex = -1
      }
      this.selected = null
    }

    // `column` lands on that column of the target row instead of stepping sideways (the Tab-Enter
    // return). `tab` moves to the cell Tab goes to, see tabTarget.
    moveSelection(rowStep, columnStep, { column = null, tab = false } = {}) {
      const cell = this.selected && this.findCell(this.selected)
      if (!cell) return
      if (tab) return this.select(this.tabTarget(cell, columnStep < 0))

      const row = cell.closest("tr")
      const rows = this.rows
      const rowIndex = rows.indexOf(row)
      // The selected row was hidden under it (its group collapsed): there is no "next" to step to,
      // and rows[0] would throw the selection to the top of the table.
      if (rowIndex === -1) return
      const cells = this.cellsOf(row)
      const columnIndex = cells.indexOf(cell)

      const targetRow = rows[Math.min(Math.max(rowIndex + rowStep, 0), rows.length - 1)]
      if (!targetRow) return

      const targetCells = this.cellsOf(targetRow)
      if (column) {
        const named = targetCells.find((candidate) => candidate.dataset.column === column)
        if (named) return this.select(named)
      }
      const index = Math.min(Math.max(columnIndex + columnStep, 0), targetCells.length - 1)
      this.select(targetCells[index])
    }

    // The cell Tab moves to from `cell`: the next one in its row, or the next editable one when the
    // table asks for it. Null at the end of the row — Tab then leaves the table as it always did,
    // so a keyboard user is never trapped inside it.
    //
    // A row with no editable cell at all (a read-only document, a Titel) has nothing to skip to, so
    // Tab steps cell by cell there instead of leaving the table from its first cell.
    tabTarget(cell, backwards) {
      const cells = this.cellsOf(cell.closest("tr"))
      const step = backwards ? -1 : 1
      const skip =
        this.tabSkipValue === "editable" &&
        cells.some((candidate) => candidate.hasAttribute("data-editable"))
      for (let index = cells.indexOf(cell) + step; cells[index]; index += step) {
        if (!skip || cells[index].hasAttribute("data-editable")) return cells[index]
      }
      return null
    }

    tabMove(backwards) {
      return [0, backwards ? -1 : 1, { tab: true }]
    }

    // Where Enter goes once a cell is committed. After a run of Tabs in this row it returns to the
    // column the run started in; Shift+Enter moves up without that return, as in Excel. The origin
    // is not consumed here: the move may yet be dropped (a rejected save), and once it lands in
    // the next row the origin no longer matches the selection anyway.
    enterMove(backwards) {
      const origin = this.tabOrigin
      const column = !backwards && origin?.rowId === this.selected?.rowId ? origin.column : null
      return [backwards ? -1 : 1, 0, { column }]
    }

    // Remembers where a run of Tabs began, so a following Enter can return there.
    startTabRun() {
      if (!this.selected || this.tabOrigin?.rowId === this.selected.rowId) return
      this.tabOrigin = { ...this.selected }
    }

    // Arrow keys, Home/End and Tab on a selected (not editing) cell. True when the key moved the
    // selection; Tab past the row's last cell is left to the browser, which takes focus on to the
    // next control on the page.
    navigationKey(event, cell) {
      if (MOVES[event.key]) {
        event.preventDefault()
        this.tabOrigin = null
        this.moveSelection(...MOVES[event.key])
      } else if (event.key === "Home" || event.key === "End") {
        event.preventDefault()
        this.tabOrigin = null
        this.moveSelection(0, event.key === "Home" ? -999 : 999)
      } else if (event.key === "Tab") {
        if (!this.tabTarget(cell, event.shiftKey)) return false
        event.preventDefault()
        this.startTabRun()
        this.moveSelection(...this.tabMove(event.shiftKey))
      } else {
        return false
      }
      return true
    }

    // A key that types a character. "@", "€", "[" and "|" need AltGr on a German Windows keyboard
    // (reported as Ctrl+Alt) and Option on a Mac, so rejecting every Alt press made them impossible
    // to type-over with. Plain Ctrl or Cmd is a shortcut; Alt on its own is one only off the Mac.
    //
    // Windows reports AltGraph for ANY Ctrl+Alt chord, and a Ctrl+Alt shortcut on a key with no
    // AltGr character arrives as its plain letter or digit. A layout's AltGr and Option characters
    // are never plain ASCII letters or digits, so those are still read as shortcuts. Whitespace is
    // refused too: Option+Space is a non-breaking space nobody means to type over a cell with.
    printable(event) {
      if (event.key.length !== 1 || event.metaKey) return false

      const altGraph = event.getModifierState?.("AltGraph")
      if (event.ctrlKey && !altGraph) return false
      if (!event.altKey && !altGraph) return true

      return (altGraph || MAC) && !MODIFIED_NON_CHARACTER.test(event.key)
    }
  }
